import { MultipleChoiceQuizStep, StepComponentProps } from '../../../@types'
import React, { useState, useMemo, useEffect } from 'react'
import { Set } from 'immutable'
import Actions from '@components/quiz/actions'
import { MapSelectOptions } from '../../SelectOption'
import Icon from '../../Icon'
import CheckMark from '@components/CheckMark'

type Props = StepComponentProps<MultipleChoiceQuizStep, Array<string>>

const OTHER_PREFIX = 'other:'

export default function MultipleChoice(
  props: Props,
): React.ReactElement<Props> {
  const { value, onChange, step } = props
  const [values, setValues] = useState(Set<string>([]))
  const [otherValue, setOtherValue] = useState<string | undefined>()
  const showOtherField = useMemo(() => values.includes('other'), [values])

  const nextEnabled = useMemo(
    () =>
      showOtherField ? !!otherValue && otherValue?.length > 0 : values.size > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, otherValue],
  )

  useEffect(() => {
    if (Array.isArray(value)) {
      setValues(Set<string>(value.filter(s => !s.startsWith(OTHER_PREFIX))))
      setOtherValue(
        value.find(s => s.startsWith(OTHER_PREFIX))?.replace(OTHER_PREFIX, ''),
      )
    }
  }, [value])

  const handleInputChange = (value: string, selected: boolean) => {
    let newValues
    if (selected) {
      newValues = value === 'none' ? values.clear() : values.delete('none')
      newValues = newValues.add(value)
    } else {
      newValues = values.delete(value)
    }
    setValues(newValues)
  }

  const nextHandler = () => {
    const newValue =
      otherValue && otherValue?.length > 0
        ? values.add(`other:${otherValue}`)
        : values
    onChange(step.id, newValue.toArray())
  }

  const options = step.options.map(option => ({
    value: option.value,
    content: (
      <div className="w-full flex gap-2.5 items-center">
        <CheckMark selected={values.includes(option.value)} />
        {option.icon && (
          <div>
            <Icon
              width="40"
              height="40"
              className="mx-auto"
              name={option.icon}
            />
          </div>
        )}
        <div className="">
          <p>{option.name}</p>
          {option.description && (
            <p className="text-gray-400 text-xs md:text-sm font-normal mt-0.5">
              {option.description}
            </p>
          )}
          {showOtherField && option.showOther && (
            <div className="mt-2">
              {
                <input
                  type="text"
                  className="border-1 border-gray-400 rounded w-full"
                  id="other-field-input"
                  min="50"
                  max="800"
                  onChange={e => setOtherValue(e.currentTarget.value)}
                  placeholder="Type it here"
                  value={otherValue}
                />
              }
            </div>
          )}
        </div>
      </div>
    ),
    selected: values.includes(option.value),
    onClick: handleInputChange,
  }))

  return (
    <>
      <div className="flex flex-wrap min-w-full max-w-2xl items-center justify-center text-sm gap-2.5 md:my-2">
        {step.columns &&
          MapSelectOptions({
            height: 'h-24 sm:h-32',
            width: 'w-1/2 md:w-1/3',
            options,
          })}
        {!step.columns &&
          MapSelectOptions({
            height: step.compact ? 'h-15' : 'h-30',
            width: 'w-full',
            options,
          })}
      </div>

      <Actions
        onNext={nextHandler}
        nextEnabled={nextEnabled}
        isLastStep={props.isLastStep}
        previousStep={props.previousStep}
        nextStep={props.nextStep}
      />
    </>
  )
}
